import * as React from "react";
import { Combobox } from "@base-ui/react/combobox";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useSeedsPortalContainer } from "./SeedsPortal";
import ComboboxItem from "./Common/ComboboxItem";
import VirtualizedComboboxList from "./Common/VirtualizedComboboxList";
import type { DataWithId } from "./Common/types";
import { groupItems } from "./Common/groupItems";
import type { PlaceholderSentinel } from "./Common/sentinels";
import {
  makePlaceholderSentinel,
  isPlaceholderSentinel,
} from "./Common/sentinels";
import { Field } from "./Common/SelectStyles";
import {
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxSeparator,
  ComboboxPositioner,
  ComboboxEmpty,
  ComboboxStatus,
  SearchableSelectTrigger,
  SearchableSelectTriggerIcon,
  SearchableSelectPlaceholder,
  SearchableSelectPopup,
  SearchableSelectSearchContainer,
  SearchableSelectInput,
  SearchableSelectList,
} from "./Common/ComboboxStyles";
import { StyledChevron, ChevronIcon } from "./Common/SelectIcons";

// ─── Props ────────────────────────────────────────────────────────────────────

interface SingleSearchableSelectBaseProps {
  id?: string;
  placeholder?: string;
  includePlaceholderItem?: boolean;
  searchPlaceholder?: string;
  emptyText?: string;
  isLoading?: boolean;
  loadingText?: string;
  disabled?: boolean;
  filter?: ((item: unknown, query: string) => boolean) | null;
  "aria-label"?: string;
  /** Id of a visible label. Preferred over `aria-label`. */
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  isInvalid?: boolean;
  required?: boolean;
  fullWidth?: boolean;
}

interface SingleSearchableSelectDataBaseProps<T extends DataWithId>
  extends SingleSearchableSelectBaseProps {
  data: T[];
  itemToString: (item: T | null) => string;
  renderItem?: (item: T) => React.ReactNode;
  renderSelection?: (item: T) => React.ReactNode;
  inputType?: "icon" | "radio" | "checkbox" | "none";
  selectedItemId?: T["id"] | null;
  defaultSelectedItemId?: T["id"] | null;
  onSelectedItemIdChange?: (id: T["id"] | null) => void;
  children?: never;
}

interface SingleSearchableSelectDataProps<T extends DataWithId>
  extends SingleSearchableSelectDataBaseProps<T> {
  isVirtualized?: false;
  groupBy?: (item: T) => string;
  renderGroupHeading?: (label: string) => React.ReactNode;
}

interface SingleSearchableSelectVirtualizedProps<T extends DataWithId>
  extends SingleSearchableSelectDataBaseProps<T> {
  isVirtualized: true;
  groupBy?: never;
  renderGroupHeading?: never;
}

interface SingleSearchableSelectChildrenProps
  extends SingleSearchableSelectBaseProps {
  children: React.ReactNode;
  selectedItemId?: string | number | null;
  defaultSelectedItemId?: string | number | null;
  onSelectedItemIdChange?: (id: string | number | null) => void;
  data?: never;
  itemToString?: never;
  renderItem?: never;
  renderSelection?: never;
  inputType?: never;
  groupBy?: never;
  renderGroupHeading?: never;
  isVirtualized?: never;
}

export type SingleSearchableSelectProps<T extends DataWithId> =
  | SingleSearchableSelectDataProps<T>
  | SingleSearchableSelectVirtualizedProps<T>
  | SingleSearchableSelectChildrenProps;

// ─── Component ────────────────────────────────────────────────────────────────

export function SingleSearchableSelect<T extends DataWithId>(
  props: SingleSearchableSelectProps<T>
) {
  const {
    id,
    placeholder = "Select...",
    includePlaceholderItem,
    searchPlaceholder = "Search...",
    emptyText = "No results found.",
    isLoading = false,
    loadingText = "Loading...",
    filter,
    fullWidth = true,
  } = props;
  const ariaDescribedBy = props["aria-describedby"];
  const ariaLabel = props["aria-label"];
  const ariaLabelledBy = props["aria-labelledby"];
  const isInvalid = props.isInvalid;

  const isChildrenMode = "children" in props && props.children != null;

  type DataProps =
    | SingleSearchableSelectDataProps<T>
    | SingleSearchableSelectVirtualizedProps<T>;

  const data = isChildrenMode ? ([] as T[]) : (props as DataProps).data;
  const itemToString = isChildrenMode
    ? (_item: T | null) => ""
    : (props as DataProps).itemToString;
  const renderItem = isChildrenMode
    ? undefined
    : (props as DataProps).renderItem;
  const renderSelection = isChildrenMode
    ? undefined
    : (props as DataProps).renderSelection;
  const inputType = isChildrenMode
    ? "icon"
    : (props as DataProps).inputType ?? "icon";
  const isVirtualized = isChildrenMode
    ? false
    : (props as DataProps).isVirtualized ?? false;
  const groupBy = isChildrenMode
    ? undefined
    : isVirtualized
    ? undefined
    : (props as SingleSearchableSelectDataProps<T>).groupBy;
  const renderGroupHeading = isChildrenMode
    ? undefined
    : isVirtualized
    ? undefined
    : (props as SingleSearchableSelectDataProps<T>).renderGroupHeading;
  const children = isChildrenMode
    ? (props as SingleSearchableSelectChildrenProps).children
    : undefined;

  const selectedItemId = props.selectedItemId;
  const defaultSelectedItemId = props.defaultSelectedItemId;
  const onSelectedItemIdChange = props.onSelectedItemIdChange;

  const { containerRef, portalContainer } = useSeedsPortalContainer();
  const [open, setOpen] = React.useState(false);
  const virtualizerRef = React.useRef<ReturnType<
    typeof useVirtualizer<HTMLDivElement, Element>
  > | null>(null);

  const isControlled = selectedItemId !== undefined;

  const idToItem = React.useCallback(
    (id: T["id"] | null | undefined): T | null => {
      if (id == null) return null;
      return data.find((d) => String(d.id) === String(id)) ?? null;
    },
    [data]
  );

  const [internalValue, setInternalValue] = React.useState<T | null>(() =>
    idToItem(defaultSelectedItemId as T["id"] | null)
  );

  const value = isControlled
    ? idToItem(selectedItemId as T["id"] | null)
    : internalValue;

  type ItemOrSentinel = T | PlaceholderSentinel;

  function handleValueChange(newItem: ItemOrSentinel | null) {
    const realItem =
      newItem == null || isPlaceholderSentinel(newItem) ? null : newItem;
    if (!isControlled) setInternalValue(realItem);
    if (onSelectedItemIdChange) {
      onSelectedItemIdChange(realItem ? realItem.id : null);
    }
  }

  const groups = React.useMemo(
    () => (groupBy ? groupItems(data, groupBy) : null),
    [data, groupBy]
  );

  return (
    <Field $fullWidth={fullWidth}>
      <div ref={containerRef} style={{ position: "relative" }} />
      <Combobox.Root
        id={id}
        value={value}
        disabled={props.disabled}
        onValueChange={handleValueChange as (item: T | null) => void}
        itemToStringLabel={(item: T | null) =>
          isPlaceholderSentinel(item) ? item.label : itemToString(item)
        }
        isItemEqualToValue={(a, b) => {
          if (isPlaceholderSentinel(a) && isPlaceholderSentinel(b)) return true;
          if (isPlaceholderSentinel(a)) return b == null;
          if (isPlaceholderSentinel(b)) return a == null;
          return a != null && b != null && a.id === b.id;
        }}
        items={
          isVirtualized
            ? includePlaceholderItem
              ? [makePlaceholderSentinel(placeholder) as unknown as T, ...data]
              : data
            : groups ?? (isChildrenMode ? undefined : data)
        }
        virtualized={isVirtualized}
        filter={filter}
        open={open}
        onOpenChange={setOpen}
        onItemHighlighted={
          isVirtualized
            ? (item, { reason, index }) => {
                const virt = virtualizerRef.current;
                if (!item || !virt) return;
                const isStart = index === 0;
                const isEnd = index === virt.options.count - 1;
                if (reason === "keyboard" && (isStart || isEnd)) {
                  queueMicrotask(() => {
                    virt.scrollToIndex(index, {
                      align: isStart ? "start" : "end",
                    });
                  });
                }
              }
            : undefined
        }
      >
        <SearchableSelectTrigger
          id={id}
          aria-describedby={ariaDescribedBy}
          aria-invalid={isInvalid || undefined}
          aria-required={props.required || undefined}
          $isInvalid={isInvalid}
        >
          <SearchableSelectPlaceholder>
            <Combobox.Value placeholder={placeholder}>
              {value
                ? renderSelection
                  ? renderSelection(value)
                  : itemToString(value)
                : placeholder}
            </Combobox.Value>
          </SearchableSelectPlaceholder>
          <SearchableSelectTriggerIcon>
            <StyledChevron data-chevron>
              <ChevronIcon />
            </StyledChevron>
          </SearchableSelectTriggerIcon>
        </SearchableSelectTrigger>

        <Combobox.Portal container={portalContainer}>
          <ComboboxPositioner sideOffset={4}>
            <SearchableSelectPopup
              aria-busy={isLoading || undefined}
              style={{ display: "flex", flexDirection: "column" }}
            >
              <SearchableSelectSearchContainer>
                <SearchableSelectInput
                  placeholder={searchPlaceholder}
                  autoFocus
                />
              </SearchableSelectSearchContainer>
              <ComboboxStatus>{isLoading ? loadingText : null}</ComboboxStatus>
              <ComboboxEmpty>{isLoading ? null : emptyText}</ComboboxEmpty>
              <SearchableSelectList
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledBy}
                style={
                  isVirtualized
                    ? {
                        padding: 0,
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                      }
                    : undefined
                }
              >
                {isVirtualized ? (
                  <VirtualizedComboboxList
                    open={open}
                    virtualizerRef={virtualizerRef}
                    value={value}
                    groups={null}
                    data={data}
                    itemToString={itemToString}
                    inputType={inputType}
                    renderItem={renderItem}
                  />
                ) : children ? (
                  children
                ) : groups ? (
                  (group: { value: string; items: T[] }, index: number) => (
                    <React.Fragment key={group.value}>
                      <ComboboxGroup items={group.items}>
                        <ComboboxGroupLabel>
                          {renderGroupHeading
                            ? renderGroupHeading(group.value)
                            : group.value}
                        </ComboboxGroupLabel>
                        <Combobox.Collection>
                          {(item: T) => (
                            <ComboboxItem
                              key={item.id}
                              value={item}
                              itemId={String(item.id)}
                              label={itemToString(item)}
                              disabled={item.disabled}
                              inputType={inputType}
                              renderItem={
                                renderItem
                                  ? () => renderItem(item)
                                  : () => itemToString(item)
                              }
                            />
                          )}
                        </Combobox.Collection>
                      </ComboboxGroup>
                      {index < groups.length - 1 && <ComboboxSeparator />}
                    </React.Fragment>
                  )
                ) : (
                  <>
                    {includePlaceholderItem && (
                      <ComboboxItem
                        key="placeholder"
                        value={
                          makePlaceholderSentinel(placeholder) as unknown as T
                        }
                        itemId="placeholder"
                        label={placeholder}
                        inputType={inputType}
                      />
                    )}
                    <Combobox.Collection>
                      {(item: T) => (
                        <ComboboxItem
                          key={item.id}
                          value={item}
                          itemId={String(item.id)}
                          label={itemToString(item)}
                          disabled={item.disabled}
                          inputType={inputType}
                          renderItem={
                            renderItem ? () => renderItem(item) : undefined
                          }
                        />
                      )}
                    </Combobox.Collection>
                  </>
                )}
              </SearchableSelectList>
            </SearchableSelectPopup>
          </ComboboxPositioner>
        </Combobox.Portal>
      </Combobox.Root>
    </Field>
  );
}
